import { IconButton, InputGroup, Paragraph } from "@smartsuite/react-ui/lib";
import { type E164Number, isValidPhoneNumber } from "libphonenumber-js";
import React, { type FC, useState } from "react";
import PhoneInput, { type Country } from "react-phone-number-input";

import * as yup from "yup";
import { FormFieldControl } from "../../../../components/FormFieldControl/FormFieldControl";
import { useFormField } from "../../../../hooks/useFormField";
import { useNormalizedMultipleValue } from "../../../../hooks/useNormalizedMultipleValue";
import { $t } from "../../../../utils/intl";
import type { FormFieldProps } from "../../../types";
import { applyYupDefaults, validateWithYup } from "../../../validator";
import { fieldsText } from "../../fields.text";
import type { PhoneFormValue } from "../phone.config";
import { AddPhone } from "./components/AddPhone";

import "./PhoneFieldControlMultiple.sass";
import { GrayColors } from "@smartsuite/types";
import { validatorText } from "../../../validator.text";

export type PhoneFieldControlMultipleProps = FormFieldProps;

export const PhoneFieldControlMultiple: FC<PhoneFieldControlMultipleProps> = ({
  caption,
  field,
  formItem,
  label,
  name,
  required,
  readOnly,
}) => {
  const [enteredValue, setEnteredValue] = useState<string | undefined>("");
  const [isFocused, setIsFocused] = useState(false);
  const defaultCountry: Country = (field.params.default_country?.toUpperCase() as Country) ?? "";

  const formField = useFormField<PhoneFormValue>({
    name,
    validate: validateWithYup(
      applyYupDefaults(
        yup
          .array()
          .test("isValidEnteredPhoneNumber", " ", () => {
            return enteredValue ? isValidPhoneNumber(enteredValue, defaultCountry) : true;
          })
          .of(
            yup.string().test("isValidPhoneNumber", " ", (value) => {
              return value ? isValidPhoneNumber(value, defaultCountry) : true;
            }),
          ),
        field,
        formItem,
      ),
    ),
  });

  // Both variants of a phone field store an array, so the single-to-multiple switch cannot leave a
  // scalar here today. Normalizing anyway keeps this control out of the BUG-21362 class: a scalar
  // from any future path would spread into characters below and be rejected by yup.array() on
  // submit, exactly as it was for the email, select and link fields.
  const phones = useNormalizedMultipleValue<string>(formField.value, formField.onChange);

  const handleChangeValue = (index: number, value: string | undefined): void => {
    const newValue = [...phones];
    newValue[index] = value ?? "";
    formField.onChange(newValue);
  };

  const handleAddNewPhone = (value: E164Number): void => {
    formField.onChange([...phones, value]);
  };

  const handleDeletePhone = (index: number): void => {
    const newValue = [...phones];
    newValue.splice(index, 1);
    formField.onChange(newValue);
  };

  const handleFocus = (): void => {
    formField.onFocus();
    setIsFocused(true);
  };

  const handleBlur = (): void => {
    formField.onBlur();
    setIsFocused(false);
  };

  return (
    <FormFieldControl
      captionAsTooltip={formItem.params.help_text_as_tooltip}
      caption={caption}
      errorMessage={formField.errorMessage}
      label={label}
      required={required}
      state={formField.state}
      readOnly={readOnly}
      helpText={formItem.params.helpText}
    >
      <div className="phone-field-control-multiple">
        {phones.map((value, index) => {
          const isValueValid = isValidPhoneNumber(value);

          return (
            <div key={index}>
              <InputGroup
                inputBackground="white-new"
                state={isFocused ? "active" : isValueValid ? "default" : "error"}
              >
                <PhoneInput
                  disabled={readOnly}
                  countryCallingCodeEditable={false}
                  defaultCountry={defaultCountry}
                  placeholder={$t(fieldsText.phonePlaceholder)}
                  value={value as E164Number}
                  onBlur={handleBlur}
                  onChange={(newValue) => handleChangeValue(index, newValue)}
                  onFocus={handleFocus}
                />
                <IconButton
                  className="phone-field-control-multiple__delete_button"
                  name="close-thin"
                  size="S"
                  ariaLabel="delete"
                  color={GrayColors["G-8"]}
                  onClick={() => handleDeletePhone(index)}
                />
              </InputGroup>

              {!isValueValid && (
                <div className="phone-field-control-multiple__error-message">
                  <Paragraph color="error" size="s">
                    {$t(validatorText.validationPhone)}
                  </Paragraph>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <AddPhone
        readOnly={readOnly}
        defaultCountry={defaultCountry}
        onAddNewPhone={handleAddNewPhone}
        forceAdding={!phones.length}
        onChangeEnteredValue={setEnteredValue}
      />
    </FormFieldControl>
  );
};
